import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { Key } from 'constants/keyboard';
import { WIDGET_MENU_OPTIONS } from 'types/WidgetMenu.types';
import { AccessibilityViewIndex } from 'types/accessibilityViewIndex';

import { closeAllRightSideMenus } from './editorSettingsSlice.helpers';
import {
  ColorVisionMode,
  DownloadModalTrigger,
  EditBackgroundType,
  EditorSettingsState,
} from './editorSettingsSlice.types';

export const initialState: EditorSettingsState = {
  zoom: 1,
  isSlideView: false,
  accessibilityViewIndex: AccessibilityViewIndex.CLOSED,
  colorVisionMode: ColorVisionMode.none,
  activeWidgetMenu: WIDGET_MENU_OPTIONS.NONE,
  replaceWidgetMenuIndex: 0,
  isWidgetSettingsView: false,
  hasKeyboardIgnore: false,
  allowedKeyboardKeys: [],
  downloadModal: {
    isOpen: false,
    trigger: DownloadModalTrigger.Default,
  },
  originalEditorId: '',

  // Saved here due to requirement on keeping the same tab when opening settings from another data table chart widget.
  chartWidgetSettingsTabIndex: 0,

  languageUpdatedOnce: false,
  brandIdx: 0,
  keyboardShortcutModalOpen: false,

  hasShowGrid: false,
  hasShowMargin: false,

  isTeamSharingEnabled: false,
  showCollaborationWarning: false,

  isUploading: false,
  editBackgroundView: undefined,

  // `isViewOnly`: Set to `true` when the application is in "view-only" mode, used for view-only sharing links
  isViewOnly: false,

  // Set to TRUE to show alt text on canvas when widgets are selected
  showAltTagOnCanvas: false,
  hasEditedAltText: false,
};

export const editorSettings = createSlice({
  name: 'editorSettings',
  initialState,
  reducers: {
    setZoom: (state, action: PayloadAction<number>) => {
      state.zoom = action.payload;
    },
    toggleSlideView: (state) => {
      const newIsSlideView = !state.isSlideView;
      if (newIsSlideView) {
        closeAllRightSideMenus(state);
      }

      state.isSlideView = newIsSlideView;
    },
    setSlideView: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeAllRightSideMenus(state);
      }

      state.isSlideView = action.payload;
    },
    setAccessibilityViewIndex: (state, action: PayloadAction<AccessibilityViewIndex>) => {
      if (action.payload !== AccessibilityViewIndex.CLOSED) {
        closeAllRightSideMenus(state);
      }

      state.accessibilityViewIndex = action.payload;
    },
    setColorVisionMode: (state, action: PayloadAction<ColorVisionMode>) => {
      state.colorVisionMode = action.payload;
    },
    dangerouslySetHasKeyboardIgnore: (
      state,
      action: PayloadAction<{ hasKeyboardIgnore: boolean; allowedKeyboardKeys?: Key[] }>,
    ) => {
      const { hasKeyboardIgnore, allowedKeyboardKeys } = action.payload;
      state.hasKeyboardIgnore = hasKeyboardIgnore;
      state.allowedKeyboardKeys = allowedKeyboardKeys || [];
    },
    setDownloadModal: (state, action: PayloadAction<{ isOpen: boolean; trigger?: DownloadModalTrigger }>) => {
      const { isOpen, trigger = DownloadModalTrigger.Default } = action.payload;
      state.downloadModal.isOpen = isOpen;
      state.downloadModal.trigger = trigger;
    },
    setOriginalEditorId: (state, action: PayloadAction<string>) => {
      state.originalEditorId = action.payload;
    },
    setWidgetSettingsView: (state, action: PayloadAction<boolean>) => {
      if (action.payload) {
        closeAllRightSideMenus(state);
      }

      state.isWidgetSettingsView = action.payload;
    },
    setEditBackgroundView: (state, action: PayloadAction<EditBackgroundType>) => {
      const editBackgroundType = action.payload;
      // Close any open side panels
      if (editBackgroundType !== undefined) {
        closeAllRightSideMenus(state);
      }

      state.editBackgroundView = editBackgroundType;
    },
    setActiveWidgetMenu: (state, action: PayloadAction<WIDGET_MENU_OPTIONS>) => {
      if (action.payload === state.activeWidgetMenu) state.activeWidgetMenu = WIDGET_MENU_OPTIONS.NONE;
      else state.activeWidgetMenu = action.payload;

      // reset the replace widget menu index
      if (state.activeWidgetMenu === WIDGET_MENU_OPTIONS.NONE && state.replaceWidgetMenuIndex !== 0) {
        state.replaceWidgetMenuIndex = 0;
      }
    },
    setChartSettingsTabIndex: (state, action: PayloadAction<{ tabIndex: number }>) => {
      const { tabIndex } = action.payload;
      state.chartWidgetSettingsTabIndex = tabIndex;
    },
    setLanguageUpdatedOnce: (state, action: PayloadAction<boolean>) => {
      state.languageUpdatedOnce = action.payload;
    },
    setBrandIdx: (state, action: PayloadAction<number>) => {
      state.brandIdx = action.payload;
    },
    setKeyboardShortcutModalOpen: (state, action: PayloadAction<boolean>) => {
      state.keyboardShortcutModalOpen = action.payload;
    },
    toggleShowGrid: (state) => {
      state.hasShowGrid = !state.hasShowGrid;
    },
    toggleShowMargin: (state) => {
      state.hasShowMargin = !state.hasShowMargin;
    },
    setTeamSharingEnabled: (state, action: PayloadAction<boolean>) => {
      state.isTeamSharingEnabled = action.payload;
    },
    setShowCollaborationWarning: (state, action: PayloadAction<boolean>) => {
      state.showCollaborationWarning = action.payload;
    },
    setIsUploading: (state, action: PayloadAction<boolean>) => {
      state.isUploading = action.payload;
    },
    setReplaceWidgetMenuIndex: (state, action: PayloadAction<number>) => {
      state.replaceWidgetMenuIndex = action.payload;
    },
    setViewOnly(state, action: PayloadAction<boolean>) {
      state.isViewOnly = action.payload;
    },
    setShowAltTagOnCanvas(state, action: PayloadAction<boolean>) {
      state.showAltTagOnCanvas = action.payload;
    },
    setHasEditedAltText(state, action: PayloadAction<boolean>) {
      state.hasEditedAltText = action.payload;
    },
  },
});

export const {
  setZoom,
  toggleSlideView,
  setAccessibilityViewIndex,
  setColorVisionMode,
  dangerouslySetHasKeyboardIgnore,
  setDownloadModal,
  setOriginalEditorId,
  setWidgetSettingsView,
  setChartSettingsTabIndex,
  setLanguageUpdatedOnce,
  setActiveWidgetMenu,
  setBrandIdx,
  setSlideView,
  setKeyboardShortcutModalOpen,
  toggleShowGrid,
  setTeamSharingEnabled,
  setShowCollaborationWarning,
  setIsUploading,
  setEditBackgroundView,
  toggleShowMargin,
  setReplaceWidgetMenuIndex,
  setViewOnly,
  setShowAltTagOnCanvas,
  setHasEditedAltText,
} = editorSettings.actions;

export default editorSettings.reducer;
